/**
 * Full profile modal reused from Tutors/Students management.
 */
import { DetailsViewModal } from '@/components/common/details/DetailsViewModal';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { TutorDetails } from '@/components/tutors/remote/renderTutorDetails';
import { StudentDetails } from '@/components/students/remote/renderStudentDetails';
import {
  useGetTutorRemoteFullState,
  useGetStudentRemoteFullState,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';

/** Props for {@link MatchingProfileModal}. */
interface MatchingProfileModalProps {
  readonly userId: string | null;
  readonly perspective: 'tutor' | 'student';
  readonly onClose: () => void;
}

/** Loads the full tutor or student record and reuses the management-page profile body. */
export function MatchingProfileModal({ userId, perspective, onClose }: MatchingProfileModalProps) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';

  const tutorQuery = useGetTutorRemoteFullState(userId ?? '', companyId, {
    enabled: perspective === 'tutor' && !!userId,
  });
  const studentQuery = useGetStudentRemoteFullState(userId ?? '', companyId, {
    enabled: perspective === 'student' && !!userId,
  });

  const isLoading =
    perspective === 'tutor' ? tutorQuery.isLoading : studentQuery.isLoading;
  const tutorRecord = perspective === 'tutor' ? tutorQuery.data?.record : undefined;
  const studentRecord = perspective === 'student' ? studentQuery.data?.record : undefined;

  return (
    <DetailsViewModal isOpen={!!userId} onClose={onClose}>
      <div className="relative min-h-[400px]">
        <LoadingOverlay
          isLoading={isLoading || (!tutorRecord && !studentRecord)}
          message="Chargement du profil..."
        />
        {!isLoading && tutorRecord && <TutorDetails record={tutorRecord} />}
        {!isLoading && studentRecord && <StudentDetails record={studentRecord} />}
      </div>
    </DetailsViewModal>
  );
}
